/**
 * Spec 048: how a finding of «Радар» reads. Plain functions: the tests run this module as it is.
 */
import type { RadarItemOut, RadarKind, RadarRing, RadarTrust } from "@/api/radar";

export const KIND_LABEL: Record<RadarKind, string> = {
  repo: "репозиторий",
  service: "сервис",
  skill: "скилл",
  mcp: "MCP",
  package: "пакет",
  app: "приложение",
  model: "модель",
  method: "приём",
  visual: "визуал",
};

export const RING_LABEL: Record<RadarRing, string> = {
  adopt: "Берём",
  trial: "Пробуем",
  assess: "Смотрим",
  hold: "Устарело",
};

export const TRUST_LABEL: Record<RadarTrust, string> = {
  rumor: "слух",
  confirmed: "подтверждено",
  tried: "опробовано",
};

export type Tone = "done" | "waiting" | "needs" | "blocked" | "review";

export function ringTone(ring: RadarRing | null): Tone {
  if (ring === "adopt") return "done";
  if (ring === "trial") return "review";
  if (ring === "hold") return "blocked";
  return "waiting";
}

export function trustTone(trust: RadarTrust): Tone {
  return trust === "tried" ? "done" : trust === "confirmed" ? "review" : "needs";
}

/** «Берём», «на разборе», «отклонена», «слита». */
export function placeLabel(item: Pick<RadarItemOut, "state" | "ring">): string {
  if (item.state === "rejected") return "отклонена";
  if (item.state === "merged") return "слита";
  return item.ring ? RING_LABEL[item.ring] : "на разборе";
}

const METRIC: Record<string, string> = {
  stars: "★",
  forks: "форков",
  issues: "issues",
  commits_month: "коммитов за месяц",
  views: "просмотров",
  likes: "лайков",
  comments: "комментариев",
  reposts: "репостов",
  downloads: "скачиваний",
};

function short(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(n >= 10_000_000 ? 0 : 1)} млн`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(n >= 10_000 ? 0 : 1)} тыс`;
  return String(n);
}

/** «★ 12 тыс · 340 форков». */
export function metricsLine(metrics: Record<string, number> | null | undefined): string {
  return Object.entries(metrics ?? {})
    .filter(([, v]) => typeof v === "number")
    .map(([k, v]) => (k === "stars" ? `★ ${short(v)}` : `${short(v)} ${METRIC[k] ?? k}`))
    .join(" · ");
}

/** «репозиторий · 3 упоминания · v1.4.0». */
export function itemLine(item: Pick<RadarItemOut, "kind" | "mentions" | "latest_version">): string {
  const parts = [KIND_LABEL[item.kind]];
  if (item.mentions) parts.push(mentionsLabel(item.mentions));
  if (item.latest_version) parts.push(item.latest_version);
  return parts.join(" · ");
}

export function mentionsLabel(n: number): string {
  const tens = n % 100;
  const ones = n % 10;
  const word = tens >= 11 && tens <= 14 ? "упоминаний" : ones === 1 ? "упоминание" : ones >= 2 && ones <= 4 ? "упоминания" : "упоминаний";
  return `${n} ${word}`;
}

/** Why a finding asks for attention now; empty when it does not. */
export function attention(item: Pick<RadarItemOut, "due" | "fading">): string {
  if (item.due) return "пора перепроверить";
  if (item.fading) return "затухает";
  return "";
}

/** How many a tile counts at most: a list of the people's side gives up to 200. */
export const COUNT_LIMIT = 200;

/** The tile «Находки» in «Знания»: «3 берём · 12 на разборе»; before the counts come — what it is. */
export function findingsLine(adopt: number | undefined, review: number | undefined): string {
  if (adopt === undefined || review === undefined) return "что сотрудники нашли в интернете";
  if (!adopt && !review) return "пока пусто";
  const count = (n: number) => (n >= COUNT_LIMIT ? `${COUNT_LIMIT}+` : String(n));
  return [adopt ? `${count(adopt)} берём` : "", review ? `${count(review)} на разборе` : ""].filter(Boolean).join(" · ");
}
